import flow from 'lodash/flow';
import range from 'lodash/range';
import {
  getServerValidationErrors,
  mapPropsToValidators,
  validate,
} from '../validators';
import {
  matrixOpenValidator,
  validateOpenRequired,
  validateOpenNumeric,
  validateOpenNumericPrecision,
  validateOpenZipcode,
  validateDatetime,
  matrixDatetimeValidator,
  matrixNumericValidator,
  matrixNumericPrecisionValidator,
  matrixZipcodeValidator,
} from './validators';
import { commonQuestionState, updateWhy } from '../response/reducer';
import prefillOpen, { prefillDatetime } from '../prefillAnswers';

const prefill = (tmap, field_type, ...args) => {
  return ['date', 'time', 'datetime'].indexOf(field_type) > -1
    ? prefillDatetime(tmap, field_type, ...args)
    : prefillOpen(tmap, ...args);
};

const reducer = props => {
  return props.matrix ? matrix(props) : simple(props);
};
export default reducer;

// updaters

export const simpleUpdate = action => state => ({
  ...state,
  isTouched: true,
  value: action.value,
});

export const simpleUpdateIf = (action, test) => state =>
  test ? simpleUpdate(action)(state) : state;

export const simpleOpenUpdate = action => state => ({
  ...state,
  values: state.values.map((field, i) =>
    simpleUpdateIf(action, i === action.index)(field)
  ),
});

const touch = state => ({
  ...state,
  isTouched: true,
});

// reducers

const addSimpleState = ({
  field_type,
  number_of_fields = 1,
  validations,
  required,
  tmap,
}) => state => ({
  ...state,
  validations: validations,
  validators: mapPropsToValidators(
    {
      required: required,
      numeric: field_type === 'numeric',
      zipcode: field_type === 'zipcode_us',
      datetime: ['date', 'time', 'datetime'].indexOf(field_type) >= 0,
    },
    {
      required: validateOpenRequired(number_of_fields || 1, validations),
      numeric: validateOpenNumeric(validations),
      zipcode: validateOpenZipcode(validations),
      datetime: validateDatetime(field_type),
    }
    ),
    values: range(number_of_fields || 1).map(index => ({
      index,
      isTouched: false,
      value: prefill(tmap, field_type, index),
    })),

});

const touchAll = state => ({
  ...state,
  values: state.values.map(touch),
});

export const simple = props => (state, action) => {
  if (state === undefined) {
    return flow(
      commonQuestionState(props),
      getServerValidationErrors(props),
      addSimpleState(props)
    )({});
  }

  switch (action.type) {
    case 'DISCUSSION_CHANGE':
    case 'OPEN_CHANGE':
      return flow(simpleOpenUpdate(action), validate(action))(state);

    case 'WHY_CHANGE':
      return flow(updateWhy(action), validate(action))(state);

    case 'SUBMIT':
      return flow(touchAll, validate(action))(state);

    default:
      return state;
  }
};

// Matrix

const addMatrixState = ({
  field_type,
  number_of_fields,
  validations,
  required,
  rows,
  tmap,
}) => state => ({
  ...state,
  validations: validations,
  rows: rows.map((row, rowIndex) => ({
    name: row.name,
    index: rowIndex,
    values: range(number_of_fields).map((n, fieldIndex) => ({
      index: fieldIndex,
      isTouched: false,
      value: prefill(tmap, field_type, row.name, fieldIndex),
    })),
  })),
  validators: mapPropsToValidators(
    {
      required,
      numeric: field_type === 'numeric',
      zipcode: field_type === 'zipcode_us',
      datetime: ['date', 'time', 'datetime'].indexOf(field_type) >= 0,
    },
    {
      required: matrixOpenValidator,
      numeric: matrixNumericValidator(validations, field_type),
      zipcode: matrixZipcodeValidator(validations, field_type),
      datetime: matrixDatetimeValidator(field_type),
    }
  ),
});

const touchAllRows = state => ({
  ...state,
  rows: state.rows.map(touchAll),
});

export const matrix = props => (state, action) => {
  if (state === undefined) {
    return flow(
      commonQuestionState(props),
      getServerValidationErrors(props),
      addMatrixState(props)
    )({});
  }

  switch (action.type) {
    case 'OPEN_MATRIX_UPDATE':
      return flow(matrixUpdate(action), validate(action))(state);

    case 'OPEN_MATRIX_BLUR':
      return flow(updateMatrixIsTouched(action), validate(action))(state);

    case 'WHY_CHANGE':
      return flow(updateWhy(action), validate(action))(state);

    case 'SUBMIT':
      return flow(touchAllRows, validate(action))(state);

    default:
      return state;
  }
};

const matrixUpdate = action => state => ({
  ...state,
  rows: state.rows.map(row => updateRow(row, action)),
});

const updateRow = (state, action) =>
  state.index === action.rowIndex
    ? {
        ...state,
        values: state.values.map(field => updateFields(field, action)),
      }
    : state;

const updateFields = (state, action) =>
  state.index === action.fieldIndex
    ? {
        ...state,
        isTouched: true,
        value: action.value,
      }
    : state;

const updateMatrixIsTouched = action => state => ({
  ...state,
  rows: state.rows.map(
    row =>
      row.index === action.rowIndex
        ? {
            ...row,
            values: row.values.map(
              field =>
                field.index === action.fieldIndex
                  ? {
                      ...field,
                      isTouched: true,
                    }
                  : field
            ),
          }
        : row
  ),
});
